import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  // Lock body scroll when modal is open
  useEffect(() => {
    if (activeModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [activeModal]);
  // Close on ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActiveModal(null);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);
  const currentYear = new Date().getFullYear();
  return <>
      <footer className="bg-white py-12 border-t border-slate-100" data-id="element-32">
        <div className="max-w-7xl mx-auto px-6 text-center text-slate-400 text-sm" data-id="element-33">
          <p data-id="element-34">
            © {currentYear} The 14-Day Nervous System Reset. All rights
            reserved.
          </p>
          <div className="mt-4 flex justify-center gap-6" data-id="element-35">
            <button onClick={() => setActiveModal('privacy')} className="hover:text-teal-600 transition-colors focus:outline-none focus:text-teal-600" data-id="element-36">
              Privacy Policy
            </button>
            <button onClick={() => setActiveModal('terms')} className="hover:text-teal-600 transition-colors focus:outline-none focus:text-teal-600" data-id="element-37">
              Terms of Service
            </button>
          </div>
        </div>
      </footer>

      <AnimatePresence data-id="element-38">
        {activeModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-39">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setActiveModal(null)} className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" data-id="element-40" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.2
        }} className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[85vh] flex flex-col" data-id="element-41">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50" data-id="element-42">
                <h3 className="text-lg font-semibold text-slate-900" data-id="element-43">
                  {activeModal === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
                </h3>
                <button onClick={() => setActiveModal(null)} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors" aria-label="Close modal" data-id="element-44">
                  <X className="w-5 h-5" data-id="element-45" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 md:p-8 text-slate-600 text-sm leading-relaxed space-y-6" data-id="element-46">
                {activeModal === 'privacy' ? <PrivacyContent data-id="element-47" /> : <TermsContent data-id="element-48" />}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex justify-end" data-id="element-49">
                <button onClick={() => setActiveModal(null)} className="px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors" data-id="element-50">
                  Close
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </>;
}
function PrivacyContent() {
  return <>
      <section data-id="element-51">
        <h4 className="text-slate-900 font-semibold mb-2" data-id="element-52">
          1. Information Collection
        </h4>
        <p data-id="element-53">
          We collect information you provide directly to us when you purchase
          our digital products, including your name and email address. We use
          secure payment processors and do not store your full credit card
          information.
        </p>
      </section>
      <section data-id="element-54">
        <h4 className="text-slate-900 font-semibold mb-2" data-id="element-55">
          2. Use of Information
        </h4>
        <p data-id="element-56">
          We use the information we collect to deliver your digital products,
          send you transaction receipts, and communicate with you about your
          purchase. We may also send you updates about the product or related
          content if you have opted in to receive such communications.
        </p>
      </section>
      <section data-id="element-57">
        <h4 className="text-slate-900 font-semibold mb-2" data-id="element-58">
          3. Data Protection
        </h4>
        <p data-id="element-59">
          We implement appropriate technical and organizational measures to
          protect your personal data against unauthorized or unlawful
          processing, accidental loss, destruction, or damage.
        </p>
      </section>
      <section data-id="element-60">
        <h4 className="text-slate-900 font-semibold mb-2" data-id="element-61">4. Cookies</h4>
        <p data-id="element-62">
          We use cookies to improve your experience on our website. These small
          text files help us understand how you use our site and allow us to
          remember your preferences. You can control cookies through your
          browser settings.
        </p>
      </section>
      <section data-id="element-63">
        <h4 className="text-slate-900 font-semibold mb-2" data-id="element-64">
          5. Third-Party Services
        </h4>
        <p data-id="element-65">
          We may use third-party services for payment processing, email
          delivery, and analytics. These providers have access to your
          information only to perform these tasks on our behalf and are
          obligated not to disclose or use it for any other purpose.
        </p>
      </section>
    </>;
}
function TermsContent() {
  return <>
      <section data-id="element-66">
        <h4 className="text-slate-900 font-semibold mb-2" data-id="element-67">
          1. Acceptance of Terms
        </h4>
        <p data-id="element-68">
          By accessing and using this website and purchasing our digital
          products, you accept and agree to be bound by the terms and provision
          of this agreement.
        </p>
      </section>
      <section data-id="element-69">
        <h4 className="text-slate-900 font-semibold mb-2" data-id="element-70">2. Use License</h4>
        <p data-id="element-71">
          Permission is granted to download one copy of the materials (digital
          products) for personal, non-commercial transitory viewing only. This
          is the grant of a license, not a transfer of title, and under this
          license you may not:
        </p>
        <ul className="list-disc pl-5 mt-2 space-y-1" data-id="element-72">
          <li data-id="element-73">modify or copy the materials;</li>
          <li data-id="element-74">use the materials for any commercial purpose;</li>
          <li data-id="element-75">
            attempt to decompile or reverse engineer any software contained on
            the website;
          </li>
          <li data-id="element-76">
            remove any copyright or other proprietary notations from the
            materials; or
          </li>
          <li data-id="element-77">
            transfer the materials to another person or "mirror" the materials
            on any other server.
          </li>
        </ul>
      </section>
      <section data-id="element-78">
        <h4 className="text-slate-900 font-semibold mb-2" data-id="element-79">3. Disclaimer</h4>
        <p data-id="element-80">
          The materials on this website are provided "as is". We make no
          warranties, expressed or implied, and hereby disclaim and negate all
          other warranties, including without limitation, implied warranties or
          conditions of merchantability, fitness for a particular purpose, or
          non-infringement of intellectual property or other violation of
          rights.
        </p>
      </section>
      <section data-id="element-81">
        <h4 className="text-slate-900 font-semibold mb-2" data-id="element-82">4. Limitations</h4>
        <p data-id="element-83">
          In no event shall we or our suppliers be liable for any damages
          (including, without limitation, damages for loss of data or profit, or
          due to business interruption) arising out of the use or inability to
          use the materials on our website.
        </p>
      </section>
      <section data-id="element-84">
        <h4 className="text-slate-900 font-semibold mb-2" data-id="element-85">
          5. Medical Disclaimer
        </h4>
        <p data-id="element-86">
          The content provided in our digital products is for informational
          purposes only and is not intended to be a substitute for professional
          medical advice, diagnosis, or treatment. Always seek the advice of
          your physician or other qualified health provider with any questions
          you may have regarding a medical condition.
        </p>
      </section>
    </>;
}